// src/pages/dashboard/statistiche.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Button,
  TableRow,
  TableCell,
  Stack,
  Chip,
  Container,
  Divider,
  TextField,
} from "@mui/material";
import EuroIcon from "@mui/icons-material/Euro";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";

import {
  getUsersByRole,
  getUsersCount,
  getShapesCount,
  getPaymentsDetailed,
  PaymentRow,
} from "../../api/stats";
import { useUserProvider } from "../../providers/useUserProvider";
import SortableTable, { HeadCell } from "../../components/SortableTable";

type Period = "30" | "60" | "90" | "total" | "custom";

const fmtEUR = (v: number) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(
    v
  );

const roleLabels: Record<string, string> = {
  "1": "Admin",
  "3": "Operatore",
  "5": "Utente",
};

// 👉 stile base per tutte le card
const cardSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
  height: "100%",
};

const headerSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 1,
};

const iconBadgeSx = {
  width: 36,
  height: 36,
  borderRadius: "999px",
  border: "1px solid",
  borderColor: "divider",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "text.secondary",
};

const Statistiche: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUserProvider();
  const isAdmin = user.userType === 1;
  const creatorId = user.userId ?? null;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // periodo rapido
  const [period, setPeriod] = useState<Period>("total");

  // range personalizzato
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [usersCount, setUsersCount] = useState<number>(0);
  const [usersByRole, setUsersByRole] = useState<Record<string, number>>({});
  const [shapesCount, setShapesCount] = useState<number>(0);
  const [payRows, setPayRows] = useState<PaymentRow[]>([]);

  // mappa Period -> giorni (per “totale” e “custom” non passiamo days)
  const daysForPeriod = useMemo<number | undefined>(() => {
    if (period === "custom" || period === "total") return undefined;
    switch (period) {
      case "30":
        return 30;
      case "60":
        return 60;
      case "90":
        return 90;
      default:
        return undefined;
    }
  }, [period]);

  // carico i dati
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        // payload per i pagamenti
        let payPayload: any = {};
        if (period === "custom" && fromDate && toDate) {
          payPayload = { startDate: fromDate, endDate: toDate }; // 👈 nomi corretti
        } else if (typeof daysForPeriod === "number") {
          payPayload = { days: daysForPeriod };
        }

        const [uCount, uByRole, sCount, paidDetailed] = await Promise.all([
          getUsersCount(isAdmin, creatorId),
          getUsersByRole(isAdmin, creatorId),
          getShapesCount(isAdmin, creatorId),
          getPaymentsDetailed(payPayload),
        ]);

        setUsersCount(uCount);
        setUsersByRole(uByRole);
        setShapesCount(sCount);
        setPayRows(paidDetailed);
      } catch (e: any) {
        setErr(e?.message ?? "Errore nel caricamento delle statistiche");
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin, creatorId, daysForPeriod, period, fromDate, toDate]);

  // totale incassi nel set corrente
  const periodTotalEuro = useMemo(
    () => payRows.reduce((acc, r) => acc + (r.importo ?? 0), 0),
    [payRows]
  );

  // breakdown ruoli
  const usersBreakdownText = useMemo(() => {
    const parts: string[] = [];
    const pushIf = (id: string, etichetta: string) => {
      const v = usersByRole[id];
      if (typeof v === "number" && v > 0) parts.push(`${v} ${etichetta}`);
    };
    pushIf("1", "Admin");
    pushIf("3", "Operatori");
    pushIf("5", "Utenti");
    return parts.length ? parts.join(" · ") : "—";
  }, [usersByRole]);

  // righe tabella
  type TxTable = {
    data: string;
    nome: string;
    cognome: string;
    importo: number;
    id_utente: number;
    nome_completo: string;
  };

  const txTableRows: TxTable[] = useMemo(() => {
    return payRows.map((r) => {
      const full = (r as any).nome_completo ?? "";
      const parts = full.trim().split(/\s+/);
      const nome = parts.slice(0, -1).join(" ") || full;
      const cognome = parts.slice(-1)[0] || "";
      return {
        data: r.data,
        nome,
        cognome,
        importo: r.importo,
        id_utente: r.id_utente,
        nome_completo: (r as any).nome_completo ?? full,
      };
    });
  }, [payRows]);

  const txHeadCells: HeadCell<TxTable>[] = [
    { id: "nome", label: "Nome" },
    { id: "cognome", label: "Cognome" },
    { id: "importo", label: "Importo", numeric: true },
    { id: "data", label: "Data" },
    { id: "azioni" as any, label: "Azioni", disableSort: true },
  ];

  // applica/reset
  const handleApplyRange = () => {
    if (fromDate && toDate) setPeriod("custom");
  };
  const handleResetRange = () => {
    setFromDate("");
    setToDate("");
    setPeriod("total");
  };

  // navigation to details
  const handleOpenDetails = (row: TxTable) => {
    navigate("/transaction-details", { state: row });
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }
  if (err) {
    return (
      <Box p={3}>
        <Typography color="error">{err}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "calc(100vh - 64px)" }}>
      {/* Sezione 1: Periodo */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, pb: 1 }}>
        <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800 }}>
          Periodo
        </Typography>
        <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.9)", mb: 1 }}>
          Seleziona un intervallo temporale rapido o personalizzato per filtrare le transazioni.
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 2 }}>
        <Box sx={{ ...cardSx, p: 2, mb: 2, width: "auto" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              {(["30", "60", "90", "total"] as Period[]).map((v) => (
                <Button
                  key={v}
                  size="small"
                  variant={period === v ? "contained" : "outlined"}
                  color={period === v ? "primary" : "inherit"}
                  sx={{ borderRadius: 999, textTransform: "none" }}
                  onClick={() => setPeriod(v)}
                >
                  {v === "30"
                    ? "Ultimi 30 giorni"
                    : v === "60"
                    ? "Ultimi 60 giorni"
                    : v === "90"
                    ? "Ultimi 90 giorni"
                    : "Totale"}
                </Button>
              ))}
            </Stack>

            {/* Range personalizzato */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                label="Dal"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="Al"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                size="small"
                variant="contained"
                sx={{ textTransform: "none", borderRadius: 999 }}
                onClick={handleApplyRange}
                disabled={!fromDate || !toDate}
              >
                Applica
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{ textTransform: "none", borderRadius: 999 }}
                onClick={handleResetRange}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Container>

      {/* Sezione 2: KPI */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, pb: 1 }}>
        <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800 }}>
          Indicatori KPI
        </Typography>
        <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.9)", mb: 1 }}>
          Panoramica di incassi, utenti e composizioni corporee nel periodo selezionato.
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, p: 2 }}>
              <Box sx={headerSx}>
                <Typography variant="subtitle1">Guadagni Totali</Typography>
                <Box sx={iconBadgeSx}>
                  <EuroIcon fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                {fmtEUR(periodTotalEuro)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {period === "total"
                  ? "Totale incassi"
                  : period === "custom"
                  ? "Intervallo personalizzato"
                  : `Incassi ${
                      period === "30"
                        ? "ultimi 30 giorni"
                        : period === "60"
                        ? "ultimi 60 giorni"
                        : "ultimi 90 giorni"
                    }`}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, p: 2 }}>
              <Box sx={headerSx}>
                <Typography variant="subtitle1">Utenti</Typography>
                <Box sx={iconBadgeSx}>
                  <PeopleAltOutlinedIcon fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h4" fontWeight={700}>{usersCount}</Typography>
              <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                {Object.entries(usersByRole).map(([rid, cnt]) => (
                  <Chip
                    key={rid}
                    size="small"
                    variant="outlined"
                    label={`${cnt} ${roleLabels[rid] ?? `Ruolo ${rid}`}`}
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, p: 2 }}>
              <Box sx={headerSx}>
                <Typography variant="subtitle1">Composizioni Corporee</Typography>
                <Box sx={iconBadgeSx}>
                  <AssignmentTurnedInOutlinedIcon fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h4" fontWeight={700}>{shapesCount}</Typography>
              <Typography variant="body2" color="text.secondary">Conteggio totale shape</Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Sezione 3: Tabella */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, pb: 1 }}>
        <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800 }}>
          Transazioni
        </Typography>
        <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.9)", mb: 1 }}>
          Elenco dei pagamenti confermati nel periodo selezionato.
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card sx={{ ...cardSx }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1">Dettagli transazioni</Typography>
            <Typography variant="body2" color="text.secondary">
              {payRows.length === 0
                ? "Nessuna transazione trovata per il periodo selezionato"
                : `${payRows.length} transazioni trovate nel periodo selezionato`}
            </Typography>
          </Box>
          <Divider />
          <CardContent>
            <SortableTable<TxTable>
              rows={txTableRows}
              headCells={txHeadCells}
              renderRow={(row) => (
                <TableRow key={`${row.data}-${row.nome}-${row.cognome}-${row.importo}`}>
                  <TableCell>{row.nome}</TableCell>
                  <TableCell>{row.cognome}</TableCell>
                  <TableCell align="right">{fmtEUR(row.importo)}</TableCell>
                  <TableCell>{row.data}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityOutlinedIcon />}
                      sx={{ textTransform: "none", borderRadius: 999 }}
                      onClick={() => handleOpenDetails(row)}
                    >
                      Dettagli
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              rowsPerPageOptions={[10, 25, 50]}
              initialRowsPerPage={25}
            />
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Statistiche;
